import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface AccessCodeRecord {
  id: string;
  code: string;
  organization_id: string | null;
  used_count: number | null;
}

const InviteRequired = () => {
  const navigate = useNavigate();
  const [showAccessCodeForm, setShowAccessCodeForm] = useState(false);
  const [accessCode, setAccessCode] = useState("");
  const [isValidating, setIsValidating] = useState(false);

  const validateAccessCode = async () => {
    if (!accessCode.trim()) {
      toast.error("Please enter an access code");
      return;
    }

    setIsValidating(true);
    try {
      
      const { data, error } = await supabase
        .from("access_codes")
        .select("*")
        .eq("code", accessCode.trim())
        .eq("is_active", true)
        .maybeSingle();

      if (error) {
        console.error("Error validating access code:", error);
        toast.error("Error validating access code");
        return;
      }

      if (data) {
        
        // Valid access code - generate consistent email/password
        const cleanCode = data.code.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
        const accessCodeEmail = `access.${cleanCode}@coordi.app`;
        const accessCodePassword = `Access${cleanCode}Static`;
        
        
        // First try to sign in - user might already exist and be confirmed
        const { data: signInAttempt } = await supabase.auth.signInWithPassword({
          email: accessCodeEmail,
          password: accessCodePassword,
        });

        if (signInAttempt.session) {
          await incrementUsageCount(data);
          toast.success("Access authenticated! Redirecting to dashboard...");
          setTimeout(() => navigate("/dashboard"), 1000);
          return;
        }

        
        // Check if user already exists in our users table
        const { data: existingUserData } = await supabase
          .from("users")
          .select("id, full_name")
          .eq("organization_id", data.organization_id)
          .ilike("full_name", `Access User (${data.code.slice(0, 4)}%`)
          .maybeSingle();


        if (existingUserData) {
          
          // User exists, try to sign in
          const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
            email: accessCodeEmail,
            password: accessCodePassword,
          });

          if (authError) {
            console.error("Sign in error:", authError);
            if (authError.message.includes("Email not confirmed")) {
              toast.error("Your access account needs to be confirmed. Please check your email and click the confirmation link, then try again.");
            } else if (authError.message.includes("Invalid login credentials")) {
              toast.error("Access credentials not found. Creating new access account...");
              // User exists in our DB but not in auth - this shouldn't happen, but let's handle it
              await createNewAccessUser(data, accessCodeEmail, accessCodePassword);
            } else {
              toast.error("Sign in failed: " + authError.message);
            }
            return;
          }

          if (authData.session) {
            await incrementUsageCount(data);
            toast.success("Access authenticated! Redirecting to dashboard...");
            setTimeout(() => navigate("/dashboard"), 1000);
            return;
          }
        } else {
          await createNewAccessUser(data, accessCodeEmail, accessCodePassword);
        }
      } else {
        toast.error("Invalid access code");
      }
    } catch (error) {
      console.error("Error validating access code:", error);
      toast.error("Error validating access code");
    } finally {
      setIsValidating(false);
    }
  };

  const createNewAccessUser = async (accessCodeData: AccessCodeRecord, email: string, password: string) => {
    try {
      
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: `Access User (${accessCodeData.code.slice(0, 4)}...)`,
            organization_id: accessCodeData.organization_id,
            access_code_user: true
          }
        }
      });

      if (authError) {
        console.error("User creation error:", authError);
        if (authError.message.includes("rate limit")) {
          toast.error("Too many attempts. Please wait a moment and try again.");
        } else if (authError.message.includes("User already registered")) {
          toast.error("Access user already exists. Please try signing in instead.");
        } else {
          toast.error("Failed to create access user: " + authError.message);
        }
        return;
      }

      if (authData.user && authData.session) {
        await incrementUsageCount(accessCodeData);
        toast.success("Access authenticated! Redirecting to dashboard...");
        setTimeout(() => navigate("/dashboard"), 1000);
      } else if (authData.user) {
        // Try to sign in immediately since user should be auto-confirmed
        const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({
          email,
          password,
        });
        
        if (signInError) {
          console.error("Immediate sign in failed:", signInError);
          toast.error("User created but sign in failed. Please try entering your access code again.");
        } else if (signInData.session) {
          await incrementUsageCount(accessCodeData);
          toast.success("Access authenticated! Redirecting to dashboard...");
          setTimeout(() => navigate("/dashboard"), 1000);
        }
      }
    } catch (error) {
      console.error("Error creating access user:", error);
      toast.error("Error creating access user");
    }
  };

  const incrementUsageCount = async (accessCodeData: AccessCodeRecord) => {
    try {
      await supabase
        .from("access_codes")
        .update({ used_count: (accessCodeData.used_count ?? 0) + 1 })
        .eq("id", accessCodeData.id);
    } catch (_error) {
      // Non-blocking usage counter update failure.
      void _error;
    }
  };

  return (
    <main className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 to-black p-4">
      <Card className="w-full max-w-lg text-center bg-gray-800 border-gray-700">
        <CardHeader>
          {/* Large Centered Logo */}
          <div className="flex justify-center">
            <img 
              src={`${import.meta.env.BASE_URL}logos/coordi-logo.png`}
              alt="Coordi" 
              className="h-64 -mb-8 w-auto"
            />
          </div>
          <CardTitle className="text-2xl font-bold text-white">Access by Invite Only</CardTitle>
          <CardDescription className="text-gray-300">
            This workspace is restricted. You need a valid invite link to access the app.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-gray-400">
            If you have an invite, open the link you received to continue. If you need access, contact your
            administrator to request an invitation.
          </p>
          
          {!showAccessCodeForm ? (
            <div className="flex gap-3 justify-center">
              <Button 
                onClick={() => setShowAccessCodeForm(true)}
                className="bg-primary text-primary-foreground hover:bg-primary/90"
              >
                Enter Access Code
              </Button>
              <Link to="/">
                <Button variant="outline" className="border-gray-600 text-gray-300 hover:bg-gray-700">Back to Home</Button>
              </Link>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="space-y-2">
                <Input
                  type="text"
                  placeholder="Enter your access code"
                  value={accessCode}
                  onChange={(e) => setAccessCode(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      validateAccessCode();
                    }
                  }}
                  className="text-center bg-gray-700 border-gray-600 text-white placeholder:text-gray-400"
                />
              </div>
              <div className="flex gap-3 justify-center">
                <Button 
                  onClick={validateAccessCode}
                  disabled={isValidating}
                  className="bg-primary text-primary-foreground hover:bg-primary/90"
                >
                  {isValidating ? "Validating..." : "Continue"}
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setShowAccessCodeForm(false);
                    setAccessCode("");
                  }}
                  className="border-gray-600 text-gray-300 hover:bg-gray-700"
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </main>
  );
};

export default InviteRequired;
